import { useState } from "react";
import { useNavigate } from "react-router";
import { usePrices, useRoster, useUpdateEmployee } from "@/lib/hooks";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { formatDollars } from "@/lib/formatters";
import {
  REPLACEMENT_PENDING_LABEL,
  STATUS_LABELS,
  availableAction,
  badgeExpiry,
  formatCalendarDate,
} from "@/lib/roster";
import type { Employee } from "@/lib/api";

const BUTTON_PRIMARY =
  "rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50";
const BUTTON_QUIET =
  "text-sm font-medium text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50";

function fullName(e: Employee): string {
  return `${e.firstName} ${e.lastName}`;
}

/** What offboarding does, in the vendor's terms. The server's notice to the
 *  Safe Environment office says retrieval is the vendor's job (email.ts); this
 *  prompt must not imply the diocese collects the badge. */
const OFFBOARD_NOTICE =
  "The Safe Environment office will be notified. Retrieving the badge is your responsibility. You can reactivate them later.";

/** Active employees first, inactive last; the server's order is kept within each. */
function activeFirst(employees: Employee[]): Employee[] {
  return [...employees.filter((e) => !e.inactive), ...employees.filter((e) => e.inactive)];
}

interface RowProps {
  employee: Employee;
  badgePrice: number | undefined;
  busy: boolean;
  /** True while this row shows the inline offboard Confirm / Cancel pair. */
  confirming: boolean;
  onReplace: (id: string) => void;
  onComplete: (id: string) => void;
  onAskOffboard: (id: string) => void;
  onCancelOffboard: () => void;
  onSetInactive: (employee: Employee, inactive: boolean) => void;
}

function RosterRow({
  employee,
  badgePrice,
  busy,
  confirming,
  onReplace,
  onComplete,
  onAskOffboard,
  onCancelOffboard,
  onSetInactive,
}: RowProps): React.ReactElement {
  const action = availableAction(employee);
  const expiry = badgeExpiry(employee.clearanceDate);
  const inactive = employee.inactive === true;

  return (
    <li
      className={`flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between ${
        inactive ? "opacity-60" : ""
      }`}
    >
      <div>
        <div className="text-sm font-medium text-gray-900">{fullName(employee)}</div>
        <div className="text-sm text-gray-600">{STATUS_LABELS[employee.status]}</div>
        {expiry && <div className="text-xs text-gray-500">Expires {formatCalendarDate(expiry)}</div>}
        {inactive && <div className="text-xs font-medium text-gray-500">No longer employed</div>}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {action.kind === "replacement" && badgePrice !== undefined && (
          <button
            type="button"
            disabled={busy}
            className={BUTTON_PRIMARY}
            onClick={() => onReplace(employee.id)}
          >
            {`Order replacement badge — ${formatDollars(badgePrice)}`}
          </button>
        )}
        {action.kind === "replacement_pending" && (
          <span className="text-sm font-medium text-gray-600">{REPLACEMENT_PENDING_LABEL}</span>
        )}
        {action.kind === "completion" && (
          <button
            type="button"
            disabled={busy}
            className={BUTTON_PRIMARY}
            onClick={() => onComplete(employee.id)}
          >
            {employee.completionPriceOwed === undefined
              ? "Complete full background check"
              : `Complete full background check — ${formatDollars(employee.completionPriceOwed)}`}
          </button>
        )}
        {inactive ? (
          <button
            type="button"
            disabled={busy}
            className={BUTTON_QUIET}
            onClick={() => onSetInactive(employee, false)}
          >
            Reactivate
          </button>
        ) : confirming ? (
          <div
            role="alertdialog"
            aria-label={`Confirm: ${fullName(employee)} is no longer employed`}
            className="flex flex-col gap-2 sm:items-end"
          >
            <p className="max-w-xs text-sm text-gray-700">
              {`Mark ${fullName(employee)} as no longer employed? ${OFFBOARD_NOTICE}`}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={busy}
                className={BUTTON_PRIMARY}
                onClick={() => onSetInactive(employee, true)}
              >
                Confirm
              </button>
              <button type="button" className={BUTTON_QUIET} onClick={onCancelOffboard}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            disabled={busy}
            className={BUTTON_QUIET}
            onClick={() => onAskOffboard(employee.id)}
          >
            Mark no longer employed
          </button>
        )}
      </div>
    </li>
  );
}

export function RosterPage(): React.ReactElement {
  const navigate = useNavigate();
  const roster = useRoster();
  const prices = usePrices();
  const update = useUpdateEmployee();
  // One row at a time shows the inline offboard confirm (no native dialog: the
  // app runs in a third-party iframe, where confirm() may return false unasked).
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const goToNewOrder = (): void => void navigate("/roster/new-order");

  if (roster.isLoading) {
    return <LoadingSpinner />;
  }

  if (roster.error) {
    return (
      <ErrorMessage
        message={roster.error instanceof Error ? roster.error.message : "Failed to load your employees"}
      />
    );
  }

  const employees = roster.data?.employees ?? [];

  const handleSetInactive = (employee: Employee, inactive: boolean): void => {
    setConfirmingId(null);
    update.mutate({ id: employee.id, inactive });
  };

  return (
    <div className="mx-auto max-w-[800px]">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">My Employees</h1>
        <button type="button" className={BUTTON_PRIMARY} onClick={goToNewOrder}>
          Add employees
        </button>
      </div>

      <div className="mb-4 empty:hidden">
        <ErrorMessage
          message={update.error instanceof Error ? update.error.message : undefined}
        />
      </div>

      {employees.length === 0 ? (
        <div className="py-12 text-center">
          <h2 className="mb-2 text-lg font-semibold text-gray-900">No employees yet</h2>
          <p className="text-sm text-gray-500">
            Add the employees who need a background check and badge to get started.
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {activeFirst(employees).map((employee) => (
            <RosterRow
              key={employee.id}
              employee={employee}
              badgePrice={prices.data?.badgePrice}
              busy={update.isPending && update.variables?.id === employee.id}
              onReplace={(id) =>
                void navigate("/roster/replace", { state: { employeeIds: [id] } })
              }
              onComplete={(id) => void navigate(`/roster/complete/${encodeURIComponent(id)}`)}
              confirming={confirmingId === employee.id}
              onAskOffboard={setConfirmingId}
              onCancelOffboard={() => setConfirmingId(null)}
              onSetInactive={handleSetInactive}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
